import { useTranslation } from "react-i18next";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { Card, Col, Empty, Row, Spin, Statistic } from "antd";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import "./overview-chart-tab.scss";
import StatisticalReportCard from "../../StatisticalReportCard";
import ComboDualAxesChart from "../../../../../../components/types-of-charts/ComboDualAxesChart";
import BarChart from "../../../../../../components/types-of-charts/BarChart";
import BarStackedChart from "../../../../../../components/types-of-charts/BarStackedChart";
import DonutChart from "../../../../../../components/types-of-charts/DonutChart";
import { BarStackedChartProps, DualChart, PieChart } from "../../../../../../types/static-report";
import useChartExport from "../../../../for-instructors/components/chart/useChartExport";
import { staticReportStore } from "../../../../../../stores/stores";
import { NumberProperties } from "docx";

type Props = {
  loading: Record<string, boolean>;
  visitsAndVisitors: DualChart[];
  visitsAndVisitorsByRegister: PieChart[];
  synthesizeUniversityList: Array<any>,
  teacherByUniversity: BarStackedChartProps[],
  studentByUniversity: BarStackedChartProps[],
  courseByUniversitySponsor: BarStackedChartProps[],
  unitResources: Array<any>,
  universityVisitsTotal: number,
  universityVisitorsTotal: number,
  teacherByUniversityTotal: number,
  studentByUniversityTotal: number,
  courseByUniversitySponsorTotal: number,
  unitResourcesTotal: number,
};

const OverviewChartTab = (props: Props) => {
  const { t } = useTranslation();
  const {
    loading,
    visitsAndVisitors,
    visitsAndVisitorsByRegister,
    synthesizeUniversityList,
    teacherByUniversity,
    studentByUniversity,
    courseByUniversitySponsor,
    unitResources,
    universityVisitsTotal,
    universityVisitorsTotal,
    teacherByUniversityTotal,
    studentByUniversityTotal,
    courseByUniversitySponsorTotal,
    unitResourcesTotal,
  } = props;

  const [dataStatistical, setDataStatistical] = useState({
    totalVisit: 0,
    rateVisit: 0,
    totalLecturer: 0,
    rateLecturer: 0,
    totalStudent: 0,
    rateStudent: 0,
    totalCourse: 0,
    rateCourse: 0,
    totalResource: 0,
    rateResource: 0,
  });

  const [totalColumnValue, setTotalColumnValue] = useState(0);
  const [totalLineValue, setTotalLineValue] = useState(0);
  const [totalSeries, setTotalSeries] = useState<any>([]);

  // Hook for managing chart exports
  const { createChartRef, handleExportImage, handleExportAllImagesAsZip } =
    useChartExport();

  const { tab, setHandleExportAllImagesAsZip, exportType } =
    staticReportStore();

  useEffect(() => {
    setHandleExportAllImagesAsZip(() => handleExportAllImagesAsZip(tab));
  }, [tab, exportType]);

  useEffect(() => {
    const visit: any = synthesizeUniversityList.find((item: any) => item?.criteria === t("Lượt truy cập", "Lượt truy cập"));
    const lecturer: any = synthesizeUniversityList.find((item: any) => item?.criteria === t("Giảng viên", "Giảng viên"));
    const student: any = synthesizeUniversityList.find((item: any) => item?.criteria === t("Sinh viên", "Sinh viên"));
    const course: any = synthesizeUniversityList.find((item: any) => item?.criteria === t("Khóa học", "Khóa học"));
    const resource: any = synthesizeUniversityList.find((item: any) => item?.criteria === t("Tài nguyên", "Tài nguyên"));

    setDataStatistical({
      totalVisit: visit?.count,
      rateVisit: typeof visit?.percentage === 'number' ? visit.percentage.toFixed(2) : '0',
      totalLecturer: lecturer?.count,
      rateLecturer: typeof lecturer?.percentage === 'number' ? lecturer.percentage.toFixed(2) : '0',
      totalStudent: student?.count,
      rateStudent: typeof student?.percentage === 'number' ? student.percentage.toFixed(2) : '0',
      totalCourse: course?.count,
      rateCourse: typeof course?.percentage === 'number' ? course.percentage.toFixed(2) : '0',
      totalResource: resource?.count,
      rateResource: typeof resource?.percentage === 'number' ? resource.percentage.toFixed(2) : '0',
    });
  }, [synthesizeUniversityList]);

  useEffect(() => {
    let colTotal = 0;
    let lineTotal = 0;
    visitsAndVisitors.map((item) => {
      colTotal += item.columnValue;
      lineTotal += item.lineValue;
    });
    setTotalColumnValue(colTotal);
    setTotalLineValue(lineTotal);
  },[visitsAndVisitors]);

  useEffect(() => {
    let timesTotal = 0;
    let usersTotal = 0;
    visitsAndVisitorsByRegister.map((item: any) => {
      timesTotal += item?.values[0].count;
      usersTotal += item?.values[1].count;
    });
    setTotalSeries([timesTotal, usersTotal]);
  },[visitsAndVisitors]);

  const renderStatistical = () => {
    return (
      <Row gutter={{ xs: 8, sm: 8, md: 16, lg: 16 }} className="row-staticstical-chart">
        <Col xl={{ span: 6 }} lg={{ span: 8 }} md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }} style={{marginTop: 0, marginBottom: 24}} className="col-staticstical-chart">
          <Card bordered={false}>
            <Statistic
              className="card-statistical"
              title={t("Lượt truy cập", "Lượt truy cập")}
              value={get(dataStatistical, 'totalVisit', 0)}
              precision={2}
              valueStyle={{ fontWeight: 500, fontSize: 30 }}
              formatter={(value) => {
                const rateVisit = get(dataStatistical, 'rateVisit', 0);
                const isNegative = rateVisit < 0;
                const absRateVisit =Math.abs(rateVisit);
                return (
                  <>
                  {value}
                  <span className="percentage-difference pl-1">
                    {absRateVisit}%
                    {isNegative ? (
                      <CaretDownOutlined style={{ color: "#FF4D4F" }} />
                    ):(
                      <CaretUpOutlined style={{ color: "#52C41A" }} />
                    )}
                    </span>
                </>
                );
              }}
            />
          </Card>
        </Col>
        <Col xl={{ span: 6 }} lg={{ span: 8 }} md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }} style={{marginTop: 0, marginBottom: 24}} className="col-staticstical-chart">
          <Card bordered={false}>
            <Statistic
              className="card-statistical"
              title={t("Giảng viên", "Giảng viên")}
              value={get(dataStatistical, 'totalLecturer', 0)}
              precision={2}
              valueStyle={{ fontWeight: 500, fontSize: 30 }}
              formatter={(value) => {
                const rateLecturer = get(dataStatistical, 'rateLecturer', 0);
                const isNegative = rateLecturer < 0;
                const absRateLecturer =Math.abs(rateLecturer);
                return (
                  <>
                  {value}
                  <span className="percentage-difference pl-1">
                    {absRateLecturer}%
                    {isNegative ? (
                      <CaretDownOutlined style={{ color: "#FF4D4F" }} />
                    ):(
                      <CaretUpOutlined style={{ color: "#52C41A" }} />
                    )}
                    </span>
                </>
                );
              }}
            />
          </Card>
        </Col>
        <Col xl={{ span: 6 }} lg={{ span: 8 }} md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }} style={{marginTop: 0, marginBottom: 24}} className="col-staticstical-chart">
          <Card bordered={false}>
            <Statistic
              className="card-statistical"
              title={t("Học viên", "Học viên")}
              value={get(dataStatistical, 'totalStudent', 0)}
              precision={2}
              valueStyle={{ fontWeight: 500, fontSize: 30 }}
              formatter={(value) => {
                const rateStudent = get(dataStatistical, 'rateStudent', 0);
                const isNegative = rateStudent < 0;
                const absRateStudent =Math.abs(rateStudent);
                return (
                  <>
                  {value}
                  <span className="percentage-difference pl-1">
                    {absRateStudent}%
                    {isNegative ? (
                      <CaretDownOutlined style={{ color: "#FF4D4F" }} />
                    ):(
                      <CaretUpOutlined style={{ color: "#52C41A" }} />
                    )}
                    </span>
                </>
                );
              }}
            />
          </Card>
        </Col>
        <Col xl={{ span: 6 }} lg={{ span: 8 }} md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }} style={{marginTop: 0, marginBottom: 24}} className="col-staticstical-chart">
          <Card bordered={false}>
            <Statistic
              className="card-statistical"
              title={t("Khóa học", "Khóa học")}
              value={get(dataStatistical, 'totalCourse', 0)}
              precision={2}
              valueStyle={{ fontWeight: 500, fontSize: 30 }}
              formatter={(value) => {
                const rateCourse = get(dataStatistical, 'rateCourse', 0);
                const isNegative = rateCourse < 0;
                const absRateCourse =Math.abs(rateCourse);
                return (
                  <>
                  {value}
                  <span className="percentage-difference pl-1">
                    {absRateCourse}%
                    {isNegative ? (
                      <CaretDownOutlined style={{ color: "#FF4D4F" }} />
                    ):(
                      <CaretUpOutlined style={{ color: "#52C41A" }} />
                    )}
                    </span>
                </>
                );
              }}
            />
          </Card>
        </Col>
        <Col xl={{ span: 6 }} lg={{ span: 8 }} md={{ span: 12 }} sm={{ span: 24 }} xs={{ span: 24 }} style={{marginTop: 0, marginBottom: 24}} className="col-staticstical-chart">
          <Card bordered={false}>
            <Statistic
              className="card-statistical"
              title={t("Tài nguyên", "Tài nguyên")}
              value={get(dataStatistical, 'totalResource', 0)}
              precision={2}
              valueStyle={{ fontWeight: 500, fontSize: 30 }}
              formatter={(value) => {
                const rateResource = get(dataStatistical, 'rateResource', 0);
                const isNegative = rateResource < 0;
                const absRateResource =Math.abs(rateResource);
                return (
                  <>
                  {value}
                  <span className="percentage-difference pl-1">
                    {absRateResource}%
                    {isNegative ? (
                      <CaretDownOutlined style={{ color: "#FF4D4F" }} />
                    ):(
                      <CaretUpOutlined style={{ color: "#52C41A" }} />
                    )}
                    </span>
                </>
                );
              }}
            />
          </Card>
        </Col>
      </Row>
    )
  }
  const listColors = ["#877676", "#f0c7c7", "#FF7875", "#FFC069", "#5CDBD3", "#69C0FF", "#B37FEB", "#5e21b5", "#e7ed24"];

  return (
    <>
      {renderStatistical()}
      <Row gutter={{ xs: 8, sm: 8, md: 16, lg: 16 }}>
        <Col sm={{ span: 12 }} xs={{ span: 24 }} style={{marginTop: 0}}>
          <StatisticalReportCard
            chartName={t("Số lượt truy cập và số người truy cập", "Số lượt truy cập và số người truy cập")}
            content={
              <Spin spinning={loading.visitsAndVisitors}>
                {visitsAndVisitors.length > 0 && totalColumnValue > 0 && totalLineValue > 0 ? (
                  <ComboDualAxesChart
                    data={visitsAndVisitors}
                    columnName={`${t("Số người truy cập", "Số người truy cập")} (${totalColumnValue})`}
                    columnColor="#69C0FF"
                    lineName={`${t("Số lượt truy cập", "Số lượt truy cập")} (${totalLineValue})`}
                    lineColor="#FF7875"
                    hasTotal={false}
                    yAxisOpposite={true}
                    ref={createChartRef(tab, t("Số lượt truy cập và số người truy cập", "Số lượt truy cập và số người truy cập"))}
                  />
                ) : (
                  <Empty description={t("Không có dữ liệu", "Không có dữ liệu")} />
                )}
              </Spin>
            }
            exportOptions={[
              {
                label: t("Hình ảnh", "Hình ảnh") + " (PNG)",
                action: () =>
                  handleExportImage(tab, t("Số lượt truy cập và số người truy cập", "Số lượt truy cập và số người truy cập"), "image/png"),
              },
              {
                label: "PDF",
                action: () =>
                  handleExportImage(tab, t("Số lượt truy cập và số người truy cập", "Số lượt truy cập và số người truy cập"),
                    "application/pdf"
                  ),
              },
            ]}
          />
        </Col>
        <Col sm={{ span: 12 }} xs={{ span: 24 }} style={{marginTop: 0}}>
          <StatisticalReportCard
            chartName={t("Đối tượng truy cập", "Đối tượng truy cập")}
            content={
              <Spin spinning={loading.visitsAndVisitorsByRegister}>
                {visitsAndVisitorsByRegister?.length === 0 && totalSeries.length === 0 || totalSeries.every((item: any) => item === 0) ? (
                  <Empty description={t("Không có dữ liệu", "Không có dữ liệu")} />
                ) : (
                  <BarChart
                    data={visitsAndVisitorsByRegister}
                    colors={["#69C0FF", "#FFC069"]}
                    seriesTotal={totalSeries.length > 0 ? totalSeries : null}
                    showLegend={true}
                    hasTotal={false}
                    ref={createChartRef(tab, t("Đối tượng truy cập", "Đối tượng truy cập"))}
                  />
                )}
              </Spin>
            }
            exportOptions={[
              {
                label: t("Hình ảnh", "Hình ảnh") + " (PNG)",
                action: () =>
                  handleExportImage(tab, t("Đối tượng truy cập", "Đối tượng truy cập"), "image/png"),
              },
              {
                label: "PDF",
                action: () =>
                  handleExportImage(tab, t("Đối tượng truy cập", "Đối tượng truy cập"),
                    "application/pdf"
                  ),
              },
            ]}
          />
        </Col>
        <Col sm={{ span: 8 }} xs={{ span: 24 }}>
          <StatisticalReportCard
            chartName={t("Số lượng giảng viên theo trình độ học vấn", "Số lượng giảng viên theo trình độ học vấn")}
            content={
              <Spin spinning={loading.teacherByUniversity}>
                {teacherByUniversity.length > 0 ? (
                  <BarStackedChart
                    data={teacherByUniversity}
                    colors={listColors}
                    hasTotal={true}
                    totalData={teacherByUniversityTotal}
                    ref={createChartRef(tab, t("Số lượng giảng viên theo trình độ học vấn", "Số lượng giảng viên theo trình độ học vấn"))}
                  />
                ) : (
                  <Empty description={t("Không có dữ liệu", "Không có dữ liệu")} />
                )}
              </Spin>
            }
            exportOptions={[
              {
                label: t("Hình ảnh", "Hình ảnh") + " (PNG)",
                action: () =>
                  handleExportImage(tab, t("Số lượng giảng viên theo trình độ học vấn", "Số lượng giảng viên theo trình độ học vấn"), "image/png"),
              },
              {
                label: "PDF",
                action: () =>
                  handleExportImage(tab, t("Số lượng giảng viên theo trình độ học vấn", "Số lượng giảng viên theo trình độ học vấn"),
                    "application/pdf"
                  ),
              },
            ]}
          />
        </Col>
        <Col sm={{ span: 8 }} xs={{ span: 24 }}>
          <StatisticalReportCard
            chartName={t("Số lượng học viên", "Số lượng học viên")}
            content={
              <Spin spinning={loading.studentByUniversity}>
                {studentByUniversity.length > 0 ? (
                  <BarStackedChart
                    data={studentByUniversity}
                    colors={["#5CDBD3", "#69C0FF"]}
                    hasTotal={true}
                    totalData={studentByUniversityTotal}
                    ref={createChartRef(tab, t("Số lượng học viên", "Số lượng học viên"))}
                  />
                ) : (
                  <Empty description={t("Không có dữ liệu", "Không có dữ liệu")} />
                )}
              </Spin>
            }
            exportOptions={[
              {
                label: t("Hình ảnh", "Hình ảnh") + " (PNG)",
                action: () =>
                  handleExportImage(tab, t("Số lượng học viên", "Số lượng học viên"), "image/png"),
              },
              {
                label: "PDF",
                action: () =>
                  handleExportImage(tab, t("Số lượng học viên", "Số lượng học viên"),
                    "application/pdf"
                  ),
              },
            ]}
          />
        </Col>
        <Col sm={{ span: 8 }} xs={{ span: 24 }}>
          <StatisticalReportCard
            chartName={t("Số lượng khóa học", "Số lượng khóa học")}
            content={
              <Spin spinning={loading.courseByUniversitySponsor}>
                {courseByUniversitySponsor.length > 0 ? (
                  <BarStackedChart
                    data={courseByUniversitySponsor}
                    colors={["#5CDBD3", "#69C0FF"]}
                    hasTotal={true}
                    totalData={courseByUniversitySponsorTotal}
                    ref={createChartRef(tab, t("Số lượng khóa học", "Số lượng khóa học"))}
                  />
                ) : (
                  <Empty description={t("Không có dữ liệu", "Không có dữ liệu")} />
                )}
              </Spin>
            }
            exportOptions={[
              {
                label: t("Hình ảnh", "Hình ảnh") + " (PNG)",
                action: () =>
                  handleExportImage(tab, t("Số lượng khóa học", "Số lượng khóa học"), "image/png"),
              },
              {
                label: "PDF",
                action: () =>
                  handleExportImage(tab, t("Số lượng khóa học", "Số lượng khóa học"),
                    "application/pdf"
                  ),
              },
            ]}
          />
        </Col>
        <Col sm={{ span: 12 }} xs={{ span: 24 }} className="col-donut-chart">
          <StatisticalReportCard
            chartName={t("Số lượng tài nguyên", "Số lượng tài nguyên")}
            content={
              <Spin spinning={loading.unitResources}>
                {unitResources?.length === 0 || unitResources?.every((item: any) => item.count === 0) ? (
                  <Empty description={t("Không có dữ liệu", "Không có dữ liệu")} />
                ) : (
                  <DonutChart
                    data={unitResources}
                    title=""
                    height={300}
                    innerSize="70%"
                    size="80%"
                    colors={["#69C0FF", "#5CDBD3", "#FFC069", "#FF7875"]}
                    ref={createChartRef(tab, t("Số lượng tài nguyên", "Số lượng tài nguyên"))}
                  />
                )}
              </Spin>
            }
            exportOptions={[
              {
                label: t("Hình ảnh", "Hình ảnh") + " (PNG)",
                action: () =>
                  handleExportImage(tab, t("Số lượng tài nguyên", "Số lượng tài nguyên"), "image/png"),
              },
              {
                label: "PDF",
                action: () =>
                  handleExportImage(tab, t("Số lượng tài nguyên", "Số lượng tài nguyên"),
                    "application/pdf"
                  ),
              },
            ]}
          />
        </Col>
      </Row>
    </>
  );
};

export default OverviewChartTab;
